/**
 * Inseam Measure 3D — QO Bike Theme
 *
 * Point-cloud biomechanic scan. A particle cloud assembles into a
 * frontal human figure, a 3D book rises between the legs showing its
 * front cover, then figure + book rotate 90° to the lateral view
 * where the SVG overlay (crosshair, dimension line) measures the
 * inseam. Canvas only — no three.js.
 *
 * Phase classes on the section root drive the SVG/CSS overlay:
 *   is-p1 cloud assembles (frontal)   is-p2 book rises + rotation
 *   is-p3 wall/lock/dimension + count is-p4 result chip
 *   is-out fade before loop restart
 */
(() => {
  'use strict';

  /* ---------- Timeline ---------- */

  const PHASES = [
    { t: 150, cls: 'is-p1' },
    { t: 2300, cls: 'is-p2' },
    { t: 6600, cls: 'is-p3' },
    { t: 10100, cls: 'is-p4' },
    { t: 13900, cls: 'is-out' }
  ];
  const TOTAL = 14800;
  const ASSEMBLE_START = 150;
  const ASSEMBLE_DUR = 1900;
  const BOOK_START = 2300;   // book rises in the frontal view
  const BOOK_DUR = 1700;
  const ROTATE_START = 4400; // then everything rotates to lateral
  const ROTATE_DUR = 1900;
  const COUNT_START = 6950;
  const COUNT_DURATION = 2200;
  const CRANK_OPTIONS = [155, 160, 165, 170, 172.5, 175];
  const ALL_CLASSES = PHASES.map((p) => p.cls);

  /* ---------- Scene space (matches the SVG viewBox 420x560) ---------- */

  const VIEW_W = 420;
  const VIEW_H = 560;
  const AXIS_X = 190; // body vertical axis
  const AXIS_Y = 300;

  /* ---------- Anatomy: rings [y, wx, wz, czOffset, columnX] ----------
     Proportions follow a standard art mannequin: broad deltoids,
     defined waist, protruding glutes, separated legs with calf bulge.
     x = left/right (frontal), z = front/back (+z faces the viewer
     frontally and points right in the lateral view). */

  /* Egg-shaped head with jaw taper to the chin (sized and lifted to
     match the reference overlay) */
  const HEAD = [
    [44, 5, 6, 5, 0], [52, 13, 16, 6, 0], [60, 18, 21, 6, 0],
    [70, 21, 24.5, 6, 0], [80, 21.5, 25, 6, 0], [90, 20, 23.5, 6, 0],
    [100, 16, 19.5, 6, 0], [107, 12, 14.5, 5, 0], [112, 9, 11, 4.5, 0]
  ];
  /* Visible neck, slightly forward, blending into the trapezius base */
  const NECK = [[112, 8.5, 10, 4.5, 0], [124, 8, 9.5, 4, 0], [136, 9.5, 11, 3, 0], [144, 13, 12.5, 2, 0]];
  /* Deeper chest and glutes per the overlay correction */
  const TORSO = [
    [146, 22, 15, 0, 0], [152, 32, 19.5, 0, 0], [160, 37, 22, 0, 0],
    [170, 37.5, 24.5, 1, 0], [182, 36, 26, 2, 0], [194, 33.5, 25, 1.5, 0],
    [208, 30.5, 23.5, 0.5, 0], [222, 28, 22, 0, 0], [236, 28.5, 22.5, -2, 0],
    [250, 30, 24, -4.5, 0], [262, 31.5, 26.5, -6.5, 0], [274, 32, 28, -8, 0],
    [286, 31, 27, -7, 0], [296, 28.5, 24, -4.5, 0], [304, 24, 20, -2, 0]
  ];
  /* Rounded deltoid caps so the shoulders are not a square shelf */
  const DELT = [
    [146, 8, 9, -0.5, 36], [154, 9.5, 10.5, -0.5, 37],
    [164, 8, 9.5, 0, 37], [174, 6, 7.5, 0.5, 36]
  ];
  /* Arms hanging naturally, drifting slightly out and forward,
     hands ending at upper-mid thigh */
  const ARM = [
    [178, 6.5, 7, 0.5, 38], [200, 6, 6.5, 0, 40], [222, 5.5, 6, -0.5, 42],
    [244, 5, 5.5, 0.5, 44], [268, 4.5, 5, 1.5, 46], [290, 4, 4.5, 2.5, 47],
    [306, 4.5, 6, 3, 48], [322, 3.5, 5, 3, 48]
  ];
  /* Crotch wedge (columns start near the centre and separate),
     deeper quads per the overlay, marked knee, calf bulge, tapered
     ankle */
  const LEG = [
    [302, 13.5, 18, 1, 9], [312, 13.5, 18.5, 1.5, 12.5], [322, 13.5, 18.5, 2, 15],
    [340, 12.5, 17.5, 2, 15.5], [358, 11.5, 16, 1.5, 15.5], [376, 10.5, 13.5, 0.5, 15.5],
    [392, 9.5, 12, 0.5, 15.5], [404, 9, 11, 0, 15], [418, 9, 12, -2, 15],
    [430, 9.5, 13.5, -3.5, 15], [444, 8.5, 12, -3, 14.5], [458, 7.5, 10.5, -2.5, 14.5],
    [470, 6.5, 9, -1.5, 14], [480, 5.5, 7.5, -1, 14]
  ];
  /* Longer foot: heel, arch and toe taper */
  const FOOT = [
    [484, 5.5, 9.5, 0, 14], [489, 6, 15, 2.5, 14],
    [494, 7, 22, 4, 13.5], [498, 7.5, 27, 4, 13.5]
  ];

  const PARTS = [
    { keys: HEAD, step: 4, mirror: false, density: 1.2 },
    { keys: NECK, step: 4, mirror: false, density: 1.2 },
    { keys: TORSO, step: 4, mirror: false, density: 1.0 },
    { keys: DELT, step: 4, mirror: true, density: 1.1 },
    { keys: ARM, step: 5, mirror: true, density: 1.6 },
    { keys: LEG, step: 4.5, mirror: true, density: 1.4 },
    { keys: FOOT, step: 3.5, mirror: true, density: 1.4 }
  ];

  /* ---------- Book box (3D, drawn on canvas with the cloud) ----------
     Real-world orientation: squeezed between the thighs, covers facing
     the legs (normal ±x), spine up against the pubic datum (y=302 →
     rel +2). Frontally the viewer sees the thin page-edge strip; in
     the lateral view the full cover face shows, extending past the
     front of the thigh. */

  const BOOK = { hw: 4, h: 38, z0: -16, z1: 36, topRel: 2, drop: 150 };

  /* ---------- Helpers ---------- */

  const clamp01 = (v) => Math.min(1, Math.max(0, v));
  const easeOutCubic = (k) => 1 - Math.pow(1 - k, 3);
  const easeInOutCubic = (k) => (k < 0.5 ? 4 * k * k * k : 1 - Math.pow(-2 * k + 2, 3) / 2);

  function sampleRow(keys, y) {
    if (y <= keys[0][0]) return keys[0].slice(1);
    for (let i = 1; i < keys.length; i++) {
      if (y <= keys[i][0]) {
        const a = keys[i - 1];
        const b = keys[i];
        const k = (y - a[0]) / (b[0] - a[0]);
        return [1, 2, 3, 4].map((j) => a[j] + (b[j] - a[j]) * k);
      }
    }
    return keys[keys.length - 1].slice(1);
  }

  function buildCloud() {
    const pts = [];
    PARTS.forEach((part) => {
      const y0 = part.keys[0][0];
      const y1 = part.keys[part.keys.length - 1][0];
      for (let y = y0; y <= y1; y += part.step) {
        const [wx, wz, cz, colx] = sampleRow(part.keys, y);
        const cols = part.mirror ? [colx, -colx] : [colx];
        cols.forEach((cx) => {
          const n = Math.max(7, Math.round((wx + wz) * part.density));
          const rot = Math.random() * Math.PI * 2;
          for (let i = 0; i < n; i++) {
            if (Math.random() < 0.05) continue; // organic gaps
            const a = rot + (i / n) * Math.PI * 2;
            pts.push({
              x: cx + Math.cos(a) * wx + (Math.random() - 0.5) * 1.2,
              y: y - AXIS_Y + (Math.random() - 0.5) * 1.8,
              z: cz + Math.sin(a) * wz + (Math.random() - 0.5) * 1.2,
              sx: (Math.random() - 0.5) * 480,
              sy: (Math.random() - 0.5) * 580,
              sz: (Math.random() - 0.5) * 260,
              ph: Math.random() * Math.PI * 2,
              st: Math.random() * 0.3,
              s: 0.7 + Math.random() * 0.9
            });
          }
        });
      }
    });
    return pts;
  }

  /* ---------- Section controller ---------- */

  class Inseam3D {
    constructor(root) {
      this.root = root;
      const cfgEl = root.querySelector('[data-i3d-config]');
      this.cfg = cfgEl ? JSON.parse(cfgEl.textContent) : {};
      this.valueEl = root.querySelector('[data-i3d-value]');
      this.crankEl = root.querySelector('[data-i3d-crank]');
      this.canvas = root.querySelector('[data-i3d-canvas]');
      this.steps = [...root.querySelectorAll('[data-i3d-step]')];
      this.target = parseFloat(this.cfg.value) || 80;
      this.accent = this.cfg.accent || '#e8d178';
      this.bg = this.cfg.bg || '#000000';
      this.running = false;
      this.offset = 0;
      this.raf = 0;
      this.elapsed = 0;
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    init() {
      if (this.crankEl) {
        const crank = CRANK_OPTIONS.filter((o) => o <= this.target * 2.1).pop() || CRANK_OPTIONS[0];
        this.crankEl.textContent = crank + ' mm';
      }
      this.steps.forEach((el, i) => {
        el.addEventListener('click', () => this.seek(i));
        el.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.seek(i);
          }
        });
      });

      if (this.canvas) {
        this.ctx = this.canvas.getContext('2d');
        this.points = buildCloud();
        this.resize();
        if ('ResizeObserver' in window) {
          new ResizeObserver(() => {
            this.resize();
            if (this.reduced || !this.running) this.renderFrame(this.elapsed || TOTAL, performance.now());
          }).observe(this.canvas);
        }
      }

      if (this.reduced) {
        this.renderStatic();
        return;
      }

      const io = new IntersectionObserver(
        (entries) => entries.forEach((e) => (e.isIntersecting ? this.play() : this.pause())),
        { threshold: 0.2 }
      );
      io.observe(this.root);
    }

    resize() {
      if (!this.canvas) return;
      const rect = this.canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      this.canvas.width = Math.round(rect.width * dpr);
      this.canvas.height = Math.round(rect.height * dpr);
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      this.cw = rect.width;
      this.ch = rect.height;
      this.viewScale = rect.height / VIEW_H;
      this.viewOffX = (rect.width - VIEW_W * this.viewScale) / 2;
    }

    play() {
      if (this.running) return;
      this.running = true;
      this.t0 = performance.now() - this.offset;
      this.raf = requestAnimationFrame((n) => this.tick(n));
    }

    pause() {
      if (!this.running) return;
      this.running = false;
      cancelAnimationFrame(this.raf);
      this.offset = this.elapsed || 0;
    }

    seek(stepIdx) {
      const targets = [PHASES[0].t, PHASES[1].t, PHASES[2].t];
      const t = targets[stepIdx] != null ? targets[stepIdx] : PHASES[0].t;
      this.resetClasses();
      this.offset = t;
      this.t0 = performance.now() - t;
      if (!this.running) this.play();
    }

    tick(now) {
      if (!this.running) return;
      let elapsed = now - this.t0;
      if (elapsed >= TOTAL) {
        this.t0 = now;
        elapsed = 0;
        this.resetClasses();
      }
      this.elapsed = elapsed;

      PHASES.forEach((p) => {
        if (elapsed >= p.t) this.root.classList.add(p.cls);
      });

      const stepIdx = elapsed >= PHASES[2].t ? 2 : elapsed >= PHASES[1].t ? 1 : 0;
      this.steps.forEach((s, i) => s.classList.toggle('is-active', i === stepIdx));

      if (this.valueEl) {
        if (elapsed >= COUNT_START) {
          const k = clamp01((elapsed - COUNT_START) / COUNT_DURATION);
          this.valueEl.textContent = (this.target * easeOutCubic(k)).toFixed(1);
        } else {
          this.valueEl.textContent = '0.0';
        }
      }

      this.renderFrame(elapsed, now);
      this.raf = requestAnimationFrame((n) => this.tick(n));
    }

    /* ---------- Rendering ---------- */

    renderFrame(elapsed, now) {
      if (!this.ctx || !this.cw) return;
      const rK = clamp01((elapsed - ROTATE_START) / ROTATE_DUR);
      const theta = easeInOutCubic(rK) * Math.PI / 2;
      const cos = Math.cos(theta);
      const sin = Math.sin(theta);
      this.ctx.clearRect(0, 0, this.cw, this.ch);
      this.renderCloud(elapsed, now, cos, sin);
      this.renderBook(elapsed, cos, sin);
    }

    project(x, y, z, cos, sin) {
      const rx = x * cos + z * sin;
      const d = -x * sin + z * cos;
      const persp = 1 + d * 0.0011;
      return {
        x: (AXIS_X + rx * persp) * this.viewScale + this.viewOffX,
        y: (AXIS_Y + y * persp) * this.viewScale,
        d
      };
    }

    renderCloud(elapsed, now, cos, sin) {
      const ctx = this.ctx;
      ctx.fillStyle = this.accent;
      const aK = clamp01((elapsed - ASSEMBLE_START) / ASSEMBLE_DUR);
      const scale = this.viewScale;
      const offX = this.viewOffX;

      for (let i = 0; i < this.points.length; i++) {
        const p = this.points[i];
        const k = easeOutCubic(clamp01((aK - p.st) / (1 - p.st)));
        if (k <= 0.001 && aK === 0) continue;

        const rx = p.x * cos + p.z * sin;
        const depth = -p.x * sin + p.z * cos;
        const b = Math.sin(now * 0.0011 + p.ph) * 0.6 * k; // breathing

        const cx = p.sx + (rx + b - p.sx) * k;
        const cy = p.sy + (p.y + b * 0.5 - p.sy) * k;
        const cd = p.sz + (depth - p.sz) * k;

        const persp = 1 + cd * 0.0011;
        const sxp = (AXIS_X + cx * persp) * scale + offX;
        const syp = (AXIS_Y + cy * persp) * scale;

        const dn = clamp01((cd + 50) / 100);
        const tw = 0.8 + 0.2 * Math.sin(now * 0.002 + p.ph);
        ctx.globalAlpha = (0.32 + 0.55 * dn) * tw * (0.15 + 0.85 * k);
        const size = p.s * scale * (0.75 + 0.35 * dn);
        ctx.fillRect(sxp, syp, size, size);
      }
      ctx.globalAlpha = 1;
    }

    /* 3D book: front cover faces the viewer while frontal, the page
       block edge shows in the lateral view. Back-face culling via the
       rotated face normals; faces painter-sorted by depth. */
    renderBook(elapsed, cos, sin) {
      const k = clamp01((elapsed - BOOK_START) / BOOK_DUR);
      if (k <= 0) return;
      const ctx = this.ctx;
      const alpha = clamp01(k * 4);
      const topY = BOOK.topRel + (1 - easeInOutCubic(k)) * BOOK.drop;
      const { hw, h, z0, z1 } = BOOK;
      const P = (x, y, z) => this.project(x, y, z, cos, sin);

      const quad = (c) => {
        ctx.beginPath();
        ctx.moveTo(c[0].x, c[0].y);
        for (let i = 1; i < 4; i++) ctx.lineTo(c[i].x, c[i].y);
        ctx.closePath();
      };
      const lerp2 = (a, b, t) => ({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });

      const faces = [];
      // Frontal: thin page-edge strip between the thighs (normal +z)
      if (cos > 0.02) {
        faces.push({
          d: z1 * cos, type: 'edge',
          c: [P(-hw, topY, z1), P(hw, topY, z1), P(hw, topY + h, z1), P(-hw, topY + h, z1)]
        });
      }
      // Lateral: full front cover broadside (normal -x)
      if (sin > 0.02) {
        faces.push({
          d: hw * sin + ((z0 + z1) / 2) * cos, type: 'cover',
          c: [P(-hw, topY, z0), P(-hw, topY, z1), P(-hw, topY + h, z1), P(-hw, topY + h, z0)]
        });
      }
      faces.sort((a, b) => a.d - b.d);

      faces.forEach((f) => {
        const at = (u, v) => lerp2(lerp2(f.c[0], f.c[1], u), lerp2(f.c[3], f.c[2], u), v);
        const spineCap = () => {
          // gold binding edge along the top, pressed against the pubis
          ctx.strokeStyle = this.accent;
          ctx.lineWidth = Math.max(1.5, 2.4 * this.viewScale);
          ctx.beginPath();
          ctx.moveTo(f.c[0].x, f.c[0].y);
          ctx.lineTo(f.c[1].x, f.c[1].y);
          ctx.stroke();
        };
        ctx.globalAlpha = alpha;
        if (f.type === 'cover') {
          ctx.fillStyle = this.accent;
          quad(f.c);
          ctx.fill();
          // cover design: dark label panel + two title lines
          ctx.fillStyle = this.bg;
          quad([at(0.16, 0.2), at(0.84, 0.2), at(0.84, 0.52), at(0.16, 0.52)]);
          ctx.fill();
          ctx.strokeStyle = this.bg;
          ctx.lineWidth = Math.max(1, 1.3 * this.viewScale);
          [0.68, 0.8].forEach((v) => {
            const a = at(0.16, v);
            const b = at(0.62, v);
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          });
        } else {
          // thin page-edge strip (frontal view)
          ctx.fillStyle = this.bg;
          quad(f.c);
          ctx.fill();
          ctx.strokeStyle = this.accent;
          ctx.lineWidth = Math.max(1, 1.2 * this.viewScale);
          quad(f.c);
          ctx.stroke();
          // pages stacked across the thickness
          ctx.globalAlpha = alpha * 0.45;
          [0.35, 0.65].forEach((u) => {
            const a = lerp2(f.c[0], f.c[1], u);
            const b = lerp2(f.c[3], f.c[2], u);
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          });
          ctx.globalAlpha = alpha;
          spineCap();
        }
      });
      ctx.globalAlpha = 1;
    }

    resetClasses() {
      this.root.classList.remove(...ALL_CLASSES);
      void this.root.offsetWidth; // force reflow so transitions replay
    }

    renderStatic() {
      this.root.classList.add('is-p1', 'is-p2', 'is-p3', 'is-p4');
      if (this.valueEl) this.valueEl.textContent = this.target.toFixed(1);
      this.steps.forEach((s, i) => s.classList.toggle('is-active', i === 2));
      this.elapsed = TOTAL;
      this.renderFrame(TOTAL, 0);
    }
  }

  const boot = () => {
    document.querySelectorAll('[data-inseam-3d]').forEach((root) => {
      if (root.dataset.i3dInit) return;
      root.dataset.i3dInit = '1';
      new Inseam3D(root).init();
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
